import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAdminCompany, updateAdminCompany,
  getSubscription,
  getCompanySubscriptions, saveCompanySystemSubscription,
  renewCompanySystemSubscription, toggleCompanySystemSubscription,
  cancelCompanySystemSubscription,
  getPayments, addPayment, updatePayment, chargePayment,
  getCompanyUsers, addCompanyUser, updateCompanyUser,
} from '../../services/adminService';
import './AdminPage.css';
import { toLocalDateInput } from '@sf/shared';

const STATUS_LABELS = { trial: 'Trial', active: 'Ativo', suspended: 'Suspenso', cancelled: 'Cancelado' };
const PAYMENT_STATUS_LABELS = { pending: 'Pendente', paid: 'Pago', failed: 'Falhou', refunded: 'Estornado', cancelled: 'Cancelado' };
const BILLING_CYCLE_LABELS = { monthly: 'Mensal', quarterly: 'Trimestral', semi_annual: 'Semestral', annual: 'Anual', custom: 'Personalizado' };

const SYSTEMS_CONFIG = [
  {
    id: 'financial',
    title: 'Sistema Financeiro',
    icon: '💼',
    badgeColor: '#10b981',
    badgeBg: '#d1fae5',
    color: '#059669',
    tag: 'Web • Porta 3000',
    description: 'Gestão financeira empresarial: receitas, despesas, conciliação e relatórios.',
  },
  {
    id: 'fleet',
    title: 'Gestão de Frotas',
    icon: '🚛',
    badgeColor: '#2563eb',
    badgeBg: '#dbeafe',
    color: '#2563eb',
    tag: 'Módulo • Porta 3002',
    description: 'Controle operacional de viagens, fretes, veículos, motoristas e abastecimentos.',
  },
  {
    id: 'task',
    title: 'Gerenciador de Tarefas',
    icon: '📋',
    badgeColor: '#8b5cf6',
    badgeBg: '#ede9fe',
    color: '#7c3aed',
    tag: 'Kanban • Porta 3004',
    description: 'Quadros visuais de trabalho, cartões sequenciais, membros e fluxo ágil.',
  },
];

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function maskCpfCnpj(value) {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR');
}

function StatusBadge({ status, type = 'sub' }) {
  if (type === 'payment') {
    return <span className={`adm-badge adm-badge-${status}`}>{PAYMENT_STATUS_LABELS[status] || status}</span>;
  }
  return <span className={`adm-badge adm-badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

// ─── Tab: Dados ─────────────────────────────────────────────────────────────
function DadosTab({ company, onUpdated }) {
  const [form, setForm] = useState({
    name: company.name || '',
    document_number: company.document_number || '',
    contact_name: company.contact_name || '',
    contact_email: company.contact_email || '',
    contact_phone: company.contact_phone || '',
    address: company.address || '',
    notes: company.notes || '',
    active: company.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = name === 'document_number'
      ? maskCpfCnpj(value)
      : (type === 'checkbox' ? checked : value);
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const updated = await updateAdminCompany(company.id, form);
      onUpdated(updated);
      setMsg({ type: 'success', text: 'Dados salvos com sucesso.' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setMsg({ type: 'error', text: errors ? Object.values(errors).flat().join(' ') : 'Erro ao salvar.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {msg && (
        <div className={`adm-alert ${msg.type === 'error' ? 'adm-alert-danger' : ''}`}
          style={msg.type === 'success' ? { background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem' } : {}}>
          {msg.text}
        </div>
      )}
      <div className="adm-card">
        <h3>Informações da Empresa</h3>
        <div className="adm-form-grid">
          <div className="adm-form-group">
            <label>Nome</label>
            <input name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="adm-form-group">
            <label>CNPJ/CPF</label>
            <input
              name="document_number"
              value={form.document_number}
              onChange={handleChange}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
            />
          </div>
          <div className="adm-form-group">
            <label>Contato</label>
            <input name="contact_name" value={form.contact_name} onChange={handleChange} />
          </div>
          <div className="adm-form-group">
            <label>E-mail do Contato</label>
            <input name="contact_email" type="email" value={form.contact_email} onChange={handleChange} />
          </div>
          <div className="adm-form-group">
            <label>Telefone</label>
            <input name="contact_phone" value={form.contact_phone} onChange={handleChange} />
          </div>
          <div className="adm-form-group">
            <label>Endereço</label>
            <input name="address" value={form.address} onChange={handleChange} />
          </div>
          <div className="adm-form-group full-width">
            <label>Notas Internas</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </div>
          <div className="adm-form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
              Empresa Ativa
            </label>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </form>
  );
}

// ─── Tab: Assinatura / Sistemas & Planos ────────────────────────────────────
function AssinaturaTab({ company, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Modal State for Link / Edit Plan
  const [planModal, setPlanModal] = useState({
    open: false,
    systemId: null,
    systemConfig: null,
    subscription: null,
    form: {
      plan_id: '',
      plan_name: '',
      access_type: 'temporary',
      status: 'active',
      starts_at: '',
      expires_at: '',
      price: '',
      billing_cycle: 'monthly',
      max_users: '',
      max_boards: '',
      notes: '',
      create_first_installment: false,
      installment_due_date: '',
    },
    saving: false,
  });

  // Modal State for Quick Renewal
  const [renewModal, setRenewModal] = useState({
    open: false,
    systemId: null,
    systemConfig: null,
    subscription: null,
    form: {
      days_to_add: 30,
      new_expires_at: '',
      create_installment: false,
      amount: '',
      due_date: '',
    },
    saving: false,
  });

  const load = useCallback(() => {
    setLoading(true);
    getCompanySubscriptions(company.id)
      .then(res => {
        setData(res);
      })
      .catch(err => {
        setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao carregar assinaturas e planos da empresa.' });
      })
      .finally(() => setLoading(false));
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  const refreshParent = () => {
    if (onUpdated) {
      getAdminCompany(company.id).then(onUpdated).catch(() => {});
    }
  };

  // ── Open Plan Modal ───────────────────────────────────────────────────────
  const handleOpenPlanModal = (sysConfig, targetPlan = null) => {
    const sysData = data?.systems?.[sysConfig.id];
    const currentSub = sysData?.subscription;
    const today = toLocalDateInput(new Date());
    const thirtyDaysFromNow = toLocalDateInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    const fiveDaysFromNow = toLocalDateInput(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000));

    let initialForm;
    if (targetPlan) {
      // Opening with a specific available plan clicked
      initialForm = {
        plan_id: targetPlan.id,
        plan_name: targetPlan.name,
        access_type: currentSub?.access_type || 'temporary',
        status: currentSub?.status || 'active',
        starts_at: currentSub?.starts_at ? currentSub.starts_at.substring(0, 10) : today,
        expires_at: currentSub?.expires_at ? currentSub.expires_at.substring(0, 10) : thirtyDaysFromNow,
        price: targetPlan.price ?? 0,
        billing_cycle: targetPlan.billing_cycle || 'monthly',
        max_users: targetPlan.max_users ?? '',
        max_boards: targetPlan.max_boards ?? '',
        notes: currentSub?.notes || '',
        create_first_installment: false,
        installment_due_date: fiveDaysFromNow,
      };
    } else if (currentSub) {
      // Editing existing subscription
      initialForm = {
        plan_id: currentSub.plan_id || '',
        plan_name: currentSub.plan_name || '',
        access_type: currentSub.access_type || 'temporary',
        status: currentSub.status || 'active',
        starts_at: currentSub.starts_at ? currentSub.starts_at.substring(0, 10) : today,
        expires_at: currentSub.expires_at ? currentSub.expires_at.substring(0, 10) : thirtyDaysFromNow,
        price: currentSub.price ?? 0,
        billing_cycle: currentSub.billing_cycle || 'monthly',
        max_users: currentSub.max_users ?? '',
        max_boards: currentSub.max_boards ?? '',
        notes: currentSub.notes || '',
        create_first_installment: false,
        installment_due_date: fiveDaysFromNow,
      };
    } else {
      // Creating new subscription for this system
      initialForm = {
        plan_id: '',
        plan_name: '',
        access_type: 'temporary',
        status: 'active',
        starts_at: today,
        expires_at: thirtyDaysFromNow,
        price: '',
        billing_cycle: 'monthly',
        max_users: '',
        max_boards: '',
        notes: '',
        create_first_installment: true,
        installment_due_date: fiveDaysFromNow,
      };
    }

    setPlanModal({
      open: true,
      systemId: sysConfig.id,
      systemConfig: sysConfig,
      subscription: currentSub,
      form: initialForm,
      saving: false,
    });
  };

  const handleSelectPlanInModal = (e) => {
    const selectedPlanId = e.target.value;
    const sysData = data?.systems?.[planModal.systemId];
    const availablePlans = sysData?.available_plans || [];
    const foundPlan = availablePlans.find(p => String(p.id) === String(selectedPlanId));

    if (foundPlan) {
      setPlanModal(prev => ({
        ...prev,
        form: {
          ...prev.form,
          plan_id: foundPlan.id,
          plan_name: foundPlan.name,
          price: foundPlan.price ?? 0,
          billing_cycle: foundPlan.billing_cycle || 'monthly',
          max_users: foundPlan.max_users ?? '',
          max_boards: foundPlan.max_boards ?? '',
        }
      }));
    } else {
      setPlanModal(prev => ({
        ...prev,
        form: {
          ...prev.form,
          plan_id: '',
          plan_name: prev.form.plan_name === 'Acesso Personalizado' ? '' : prev.form.plan_name,
        }
      }));
    }
  };

  const handlePlanFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlanModal(prev => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: type === 'checkbox' ? checked : value,
      }
    }));
  };

  const handleSavePlanModal = async (e) => {
    e.preventDefault();
    setPlanModal(prev => ({ ...prev, saving: true }));
    setMsg(null);
    try {
      const payload = {
        plan_id: planModal.form.plan_id ? parseInt(planModal.form.plan_id) : null,
        plan_name: planModal.form.plan_name || 'Acesso Personalizado',
        access_type: planModal.form.access_type,
        status: planModal.form.status,
        starts_at: planModal.form.starts_at || null,
        expires_at: planModal.form.access_type === 'temporary' ? planModal.form.expires_at : null,
        price: planModal.form.price !== '' ? parseFloat(planModal.form.price) : 0,
        billing_cycle: planModal.form.billing_cycle,
        max_users: planModal.form.max_users !== '' ? parseInt(planModal.form.max_users) : null,
        max_boards: planModal.form.max_boards !== '' ? parseInt(planModal.form.max_boards) : null,
        notes: planModal.form.notes || null,
        create_first_installment: planModal.form.create_first_installment,
        installment_due_date: planModal.form.create_first_installment ? planModal.form.installment_due_date : null,
      };

      await saveCompanySystemSubscription(company.id, planModal.systemId, payload);
      setMsg({ type: 'success', text: `Plano vinculado com sucesso para ${planModal.systemConfig.title}!` });
      setPlanModal(prev => ({ ...prev, open: false }));
      load();
      refreshParent();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao vincular plano ao sistema.' });
      setPlanModal(prev => ({ ...prev, saving: false }));
    }
  };

  // ── Open Renewal Modal ────────────────────────────────────────────────────
  const handleOpenRenewModal = (sysConfig, sub) => {
    const currentExpiry = (sub.expires_at && new Date(sub.expires_at) > new Date())
      ? new Date(sub.expires_at)
      : new Date();
    const defaultNewExpiry = new Date(currentExpiry.getTime() + 30 * 24 * 60 * 60 * 1000);
    const fiveDaysFromNow = toLocalDateInput(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000));

    setRenewModal({
      open: true,
      systemId: sysConfig.id,
      systemConfig: sysConfig,
      subscription: sub,
      form: {
        days_to_add: 30,
        new_expires_at: toLocalDateInput(defaultNewExpiry),
        create_installment: false,
        amount: sub.price ?? '',
        due_date: fiveDaysFromNow,
      },
      saving: false,
    });
  };

  const handleRenewDaysShortcut = (days) => {
    const currentExpiry = (renewModal.subscription?.expires_at && new Date(renewModal.subscription.expires_at) > new Date())
      ? new Date(renewModal.subscription.expires_at)
      : new Date();
    const newExpiry = new Date(currentExpiry.getTime() + days * 24 * 60 * 60 * 1000);
    setRenewModal(prev => ({
      ...prev,
      form: {
        ...prev.form,
        days_to_add: days,
        new_expires_at: toLocalDateInput(newExpiry),
      }
    }));
  };

  const handleRenewFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRenewModal(prev => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: type === 'checkbox' ? checked : value,
      }
    }));
  };

  const handleSaveRenewModal = async (e) => {
    e.preventDefault();
    setRenewModal(prev => ({ ...prev, saving: true }));
    setMsg(null);
    try {
      const payload = {
        days_to_add: renewModal.form.days_to_add ? parseInt(renewModal.form.days_to_add) : null,
        new_expires_at: renewModal.form.new_expires_at || null,
        create_installment: renewModal.form.create_installment,
        amount: renewModal.form.create_installment && renewModal.form.amount !== '' ? parseFloat(renewModal.form.amount) : null,
        due_date: renewModal.form.create_installment ? renewModal.form.due_date : null,
      };

      await renewCompanySystemSubscription(company.id, renewModal.systemId, payload);
      setMsg({ type: 'success', text: `Prazo renovado com sucesso para ${renewModal.systemConfig.title}!` });
      setRenewModal(prev => ({ ...prev, open: false }));
      load();
      refreshParent();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao renovar acesso.' });
      setRenewModal(prev => ({ ...prev, saving: false }));
    }
  };

  // ── Quick Toggle Status ───────────────────────────────────────────────────
  const handleToggleStatus = async (sysConfig, sub) => {
    const action = sub.status === 'active' ? 'suspender' : 'reativar';
    if (!window.confirm(`Deseja realmente ${action} o acesso ao ${sysConfig.title} para esta empresa?`)) return;
    try {
      const res = await toggleCompanySystemSubscription(company.id, sysConfig.id);
      setMsg({ type: 'success', text: res.message });
      load();
      refreshParent();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao alternar status do acesso.' });
    }
  };

  // ── Cancel Subscription ───────────────────────────────────────────────────
  const handleCancelSubscription = async (sysConfig) => {
    if (!window.confirm(`Tem certeza que deseja cancelar a assinatura do ${sysConfig.title} para esta empresa?`)) return;
    try {
      const res = await cancelCompanySystemSubscription(company.id, sysConfig.id);
      setMsg({ type: 'success', text: res.message });
      load();
      refreshParent();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao cancelar assinatura.' });
    }
  };

  if (loading) return <div className="adm-loading">Carregando planos e sistemas da empresa...</div>;

  return (
    <div>
      {msg && (
        <div
          className={`adm-alert ${msg.type === 'error' ? 'adm-alert-danger' : ''}`}
          style={msg.type === 'success' ? { background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1.25rem' } : { marginBottom: '1.25rem' }}
        >
          {msg.text}
        </div>
      )}

      <div style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem' }}>Planos Contratados por Sistema</h3>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Gerencie e vincule os planos contratados para cada um dos sistemas da plataforma ({company.name}).
        </p>
      </div>

      {/* Cards por Sistema */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {SYSTEMS_CONFIG.map(sysConfig => {
          const sysData = data?.systems?.[sysConfig.id];
          const sub = sysData?.subscription;
          const availablePlans = sysData?.available_plans || [];
          const isIntegral = sub?.access_type === 'integral';
          const isExpired = sub && !isIntegral && sub.expires_at && new Date(sub.expires_at) < new Date();

          return (
            <div key={sysConfig.id} className="adm-card" style={{ borderLeft: `5px solid ${sysConfig.color}`, padding: '1.25rem 1.5rem' }}>
              {/* Header do Sistema */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.8rem' }}>{sysConfig.icon}</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {sysConfig.title}
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: sysConfig.badgeBg, color: sysConfig.badgeColor }}>
                        {sysConfig.tag}
                      </span>
                    </h3>
                    <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>
                      {sysConfig.description}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div>
                  {sub ? (
                    isIntegral && sub.status === 'active' ? (
                      <span className="adm-badge adm-badge-active" style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        ♾️ Ativo (Integral / Vitalício)
                      </span>
                    ) : isExpired ? (
                      <span className="adm-badge adm-badge-cancelled" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        ⚠️ Expirado em {formatDate(sub.expires_at)}
                      </span>
                    ) : sub.status === 'active' ? (
                      <span className="adm-badge adm-badge-active" style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        ⏱️ Ativo até {formatDate(sub.expires_at)}
                      </span>
                    ) : sub.status === 'trial' ? (
                      <span className="adm-badge adm-badge-trial" style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        🟡 Em Trial {sub.expires_at ? `até ${formatDate(sub.expires_at)}` : ''}
                      </span>
                    ) : sub.status === 'suspended' ? (
                      <span className="adm-badge adm-badge-suspended" style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        ⛔ Acesso Suspenso
                      </span>
                    ) : (
                      <span className="adm-badge adm-badge-cancelled" style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                        ✕ Cancelado
                      </span>
                    )
                  ) : (
                    <span className="adm-badge" style={{ background: '#f3f4f6', color: '#6b7280', fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}>
                      ⚪ Não Contratado
                    </span>
                  )}
                </div>
              </div>

              {/* Informações da Assinatura Atual */}
              {sub ? (
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: '1rem', marginTop: '0.75rem', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span>📄</span> Contrato Atual Vinculado:
                  </div>
                  <div className="adm-three-col" style={{ gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Plano Contratado</div>
                      <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>
                        {sub.plan_name || 'Acesso Personalizado'}
                        {sub.plan && (
                          <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#059669', background: '#d1fae5', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                            Catálogo
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Valor & Ciclo</div>
                      <div style={{ fontWeight: 600 }}>
                        {formatCurrency(sub.price)} <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>/ {BILLING_CYCLE_LABELS[sub.billing_cycle] || sub.billing_cycle}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Modalidade de Acesso</div>
                      <div style={{ fontWeight: 600 }}>
                        {isIntegral ? '♾️ Tempo Integral (Permanente)' : '⏱️ Tempo Determinado'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Início da Vigência</div>
                      <div>{formatDate(sub.starts_at || sub.created_at)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Data de Vencimento / Expiração</div>
                      <div style={{ fontWeight: isExpired ? 700 : 500, color: isExpired ? '#dc2626' : 'inherit' }}>
                        {isIntegral ? 'Permanente (sem vencimento)' : formatDate(sub.expires_at)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Limite de Usuários</div>
                      <div>{sub.max_users ? `${sub.max_users} usuário(s)` : 'Ilimitado'}</div>
                    </div>
                    {sysConfig.id === 'task' && (
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Limite de Quadros</div>
                        <div>{sub.max_boards ? `${sub.max_boards} quadro(s)` : 'Ilimitado'}</div>
                      </div>
                    )}
                    {sub.notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Notas / Observações</div>
                        <div style={{ fontSize: '0.85rem', color: '#4b5563', background: '#fff', padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                          {sub.notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '1rem', marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    Nenhum plano contratado para o <strong>{sysConfig.title}</strong> nesta empresa.
                  </div>
                  <button
                    type="button"
                    className="adm-btn adm-btn-primary adm-btn-sm"
                    onClick={() => handleOpenPlanModal(sysConfig)}
                    style={{ background: sysConfig.color, borderColor: sysConfig.color }}
                  >
                    + Vincular Plano ao Sistema
                  </button>
                </div>
              )}

              {/* Seção: Planos Disponíveis para Contratação deste Sistema */}
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Planos Disponíveis para Contratação no {sysConfig.title}</span>
                  <Link to={`/admin/systems/${sysConfig.id}?tab=plans`} style={{ fontSize: '0.75rem', textTransform: 'none', color: '#4f46e5', textDecoration: 'none' }}>
                    + Gerenciar catálogo de planos →
                  </Link>
                </div>

                {availablePlans.length === 0 ? (
                  <div style={{ fontSize: '0.82rem', color: '#9ca3af', fontStyle: 'italic', padding: '0.5rem 0' }}>
                    Nenhum plano cadastrado no catálogo deste sistema. (Você pode vincular um acesso personalizado clicando no botão abaixo).
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
                    {availablePlans.map(p => {
                      const isCurrentPlan = sub?.plan_id === p.id;
                      return (
                        <div
                          key={p.id}
                          style={{
                            border: isCurrentPlan ? `2px solid ${sysConfig.color}` : '1px solid #e5e7eb',
                            background: isCurrentPlan ? sysConfig.badgeBg : '#fff',
                            borderRadius: 8,
                            padding: '0.75rem 1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '0.4rem',
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <strong style={{ fontSize: '0.92rem', color: '#111827' }}>{p.name}</strong>
                              {isCurrentPlan && (
                                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: sysConfig.color, background: '#fff', padding: '1px 6px', borderRadius: 10, border: `1px solid ${sysConfig.color}` }}>
                                  ✓ Atual
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', marginTop: '2px' }}>
                              {formatCurrency(p.price)}
                              <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#6b7280' }}>
                                {' '}/ {BILLING_CYCLE_LABELS[p.billing_cycle] || p.billing_cycle}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                              {p.max_users ? `${p.max_users} usuário(s)` : 'Usuários ilimitados'}
                              {p.max_boards ? ` • ${p.max_boards} quadro(s)` : ''}
                            </div>
                          </div>

                          <div style={{ marginTop: '0.5rem' }}>
                            <button
                              type="button"
                              className={`adm-btn adm-btn-sm ${isCurrentPlan ? 'adm-btn-secondary' : 'adm-btn-primary'}`}
                              onClick={() => handleOpenPlanModal(sysConfig, p)}
                              style={{ width: '100%', fontSize: '0.78rem', justifyContent: 'center' }}
                            >
                              {isCurrentPlan ? 'Reconfigurar este Plano' : 'Vincular este Plano'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Barra de Ações Rápidas */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
                <button
                  type="button"
                  className="adm-btn adm-btn-primary adm-btn-sm"
                  onClick={() => handleOpenPlanModal(sysConfig)}
                  style={{ background: sysConfig.color, borderColor: sysConfig.color }}
                >
                  {sub ? '✏️ Alterar / Vincular Outro Plano' : '+ Vincular Plano ao Sistema'}
                </button>

                {sub && !isIntegral && (
                  <button
                    type="button"
                    className="adm-btn adm-btn-secondary adm-btn-sm"
                    onClick={() => handleOpenRenewModal(sysConfig, sub)}
                  >
                    🔄 Renovar / Estender Prazo
                  </button>
                )}

                {sub && (
                  <button
                    type="button"
                    className={`adm-btn adm-btn-sm ${sub.status === 'active' ? 'adm-btn-secondary' : 'adm-btn-success'}`}
                    onClick={() => handleToggleStatus(sysConfig, sub)}
                  >
                    {sub.status === 'active' ? '⏸️ Suspender Acesso' : '▶️ Reativar Acesso'}
                  </button>
                )}

                {sub && sub.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="adm-btn adm-btn-danger adm-btn-sm"
                    onClick={() => handleCancelSubscription(sysConfig)}
                    style={{ marginLeft: 'auto' }}
                  >
                    ✕ Cancelar Assinatura
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: VINCULAR / ALTERAR PLANO                                     */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {planModal.open && (
        <div className="adm-modal-overlay" onClick={() => setPlanModal(prev => ({ ...prev, open: false }))}>
          <div className="adm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="adm-modal-header">
              <h3>
                {planModal.subscription ? 'Editar / Vincular Plano' : 'Vincular Plano'} — {planModal.systemConfig.title}
              </h3>
              <button className="adm-modal-close" onClick={() => setPlanModal(prev => ({ ...prev, open: false }))}>✕</button>
            </div>

            <form onSubmit={handleSavePlanModal}>
              <div className="adm-modal-body">
                <div className="adm-form-grid">
                  {/* Seleção do Plano do Sistema */}
                  <div className="adm-form-group full-width">
                    <label style={{ fontWeight: 600 }}>Plano Pré-Cadastrado do Sistema</label>
                    <select
                      value={planModal.form.plan_id || ''}
                      onChange={handleSelectPlanInModal}
                    >
                      <option value="">-- Personalizado / Sem plano pré-definido --</option>
                      {(data?.systems?.[planModal.systemId]?.available_plans || []).map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {formatCurrency(p.price)} ({BILLING_CYCLE_LABELS[p.billing_cycle] || p.billing_cycle})
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Ao escolher um plano do catálogo, os valores padrão de preço, ciclo e limites são carregados automaticamente.
                    </span>
                  </div>

                  {/* Nome do Plano */}
                  <div className="adm-form-group">
                    <label>Nome do Plano *</label>
                    <input
                      name="plan_name"
                      value={planModal.form.plan_name}
                      onChange={handlePlanFormChange}
                      placeholder="Ex: Starter, Pro, Acesso Integral..."
                      required
                    />
                  </div>

                  {/* Valor Cobrado */}
                  <div className="adm-form-group">
                    <label>Valor (R$) *</label>
                    <input
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={planModal.form.price}
                      onChange={handlePlanFormChange}
                      required
                    />
                  </div>

                  {/* Ciclo de Cobrança */}
                  <div className="adm-form-group">
                    <label>Ciclo de Cobrança</label>
                    <select name="billing_cycle" value={planModal.form.billing_cycle} onChange={handlePlanFormChange}>
                      <option value="monthly">Mensal</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="semi_annual">Semestral</option>
                      <option value="annual">Anual</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="adm-form-group">
                    <label>Status do Acesso</label>
                    <select name="status" value={planModal.form.status} onChange={handlePlanFormChange}>
                      <option value="active">Ativo (Liberado)</option>
                      <option value="trial">Período de Testes (Trial)</option>
                      <option value="suspended">Suspenso (Bloqueado)</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>

                  {/* Modalidade de Acesso */}
                  <div className="adm-form-group full-width" style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <label style={{ fontWeight: 700, marginBottom: '0.4rem', display: 'block' }}>
                      Tipo de Liberação de Acesso *
                    </label>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="access_type"
                          value="integral"
                          checked={planModal.form.access_type === 'integral'}
                          onChange={handlePlanFormChange}
                        />
                        <strong>♾️ Tempo Integral</strong> (permanente / vitalício sem expiração)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="access_type"
                          value="temporary"
                          checked={planModal.form.access_type === 'temporary'}
                          onChange={handlePlanFormChange}
                        />
                        <strong>⏱️ Tempo Determinado</strong> (com validade e renovações)
                      </label>
                    </div>
                  </div>

                  {/* Vigência: Início e Fim */}
                  <div className="adm-form-group">
                    <label>Início da Vigência</label>
                    <input
                      name="starts_at"
                      type="date"
                      value={planModal.form.starts_at}
                      onChange={handlePlanFormChange}
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>
                      Data de Expiração {planModal.form.access_type === 'temporary' ? '*' : '(Ignorado no Integral)'}
                    </label>
                    <input
                      name="expires_at"
                      type="date"
                      value={planModal.form.expires_at}
                      onChange={handlePlanFormChange}
                      disabled={planModal.form.access_type === 'integral'}
                      required={planModal.form.access_type === 'temporary'}
                    />
                  </div>

                  {/* Limites */}
                  <div className="adm-form-group">
                    <label>Máx. Usuários (vazio = ilimitado)</label>
                    <input
                      name="max_users"
                      type="number"
                      min="1"
                      placeholder="Ilimitado"
                      value={planModal.form.max_users}
                      onChange={handlePlanFormChange}
                    />
                  </div>

                  {planModal.systemId === 'task' && (
                    <div className="adm-form-group">
                      <label>Máx. Quadros Kanban (vazio = ilimitado)</label>
                      <input
                        name="max_boards"
                        type="number"
                        min="1"
                        placeholder="Ilimitado"
                        value={planModal.form.max_boards}
                        onChange={handlePlanFormChange}
                      />
                    </div>
                  )}

                  {/* Primeira Parcela */}
                  {!planModal.subscription && (
                    <div className="adm-form-group full-width" style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#166534' }}>
                        <input
                          type="checkbox"
                          name="create_first_installment"
                          checked={planModal.form.create_first_installment}
                          onChange={handlePlanFormChange}
                        />
                        Gerar primeira parcela de cobrança para esta contratação
                      </label>
                      {planModal.form.create_first_installment && (
                        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.8rem', color: '#166534' }}>Data de vencimento:</span>
                          <input
                            name="installment_due_date"
                            type="date"
                            value={planModal.form.installment_due_date}
                            onChange={handlePlanFormChange}
                            style={{ maxWidth: 160, padding: '0.25rem 0.5rem' }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notas */}
                  <div className="adm-form-group full-width">
                    <label>Notas Internas</label>
                    <textarea
                      name="notes"
                      value={planModal.form.notes}
                      onChange={handlePlanFormChange}
                      rows={2}
                      placeholder="Observações contratuais, detalhes de negociação..."
                    />
                  </div>
                </div>
              </div>

              <div className="adm-modal-footer">
                <button
                  type="button"
                  className="adm-btn adm-btn-secondary"
                  onClick={() => setPlanModal(prev => ({ ...prev, open: false }))}
                  disabled={planModal.saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="adm-btn adm-btn-primary"
                  disabled={planModal.saving}
                  style={{ background: planModal.systemConfig.color, borderColor: planModal.systemConfig.color }}
                >
                  {planModal.saving ? 'Salvando...' : 'Salvar e Vincular Plano'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: RENOVAR PRAZO DE ACESSO                                      */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {renewModal.open && (
        <div className="adm-modal-overlay" onClick={() => setRenewModal(prev => ({ ...prev, open: false }))}>
          <div className="adm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="adm-modal-header">
              <h3>Renovar Prazo — {renewModal.systemConfig.title}</h3>
              <button className="adm-modal-close" onClick={() => setRenewModal(prev => ({ ...prev, open: false }))}>✕</button>
            </div>

            <form onSubmit={handleSaveRenewModal}>
              <div className="adm-modal-body">
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#1e40af' }}>
                    <strong>Plano Atual:</strong> {renewModal.subscription.plan_name || 'Acesso Personalizado'}
                    <br />
                    <strong>Expiração Atual:</strong> {formatDate(renewModal.subscription.expires_at)}
                  </div>
                </div>

                <div className="adm-form-group">
                  <label style={{ fontWeight: 600 }}>Prorrogar Validade por:</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                    {[30, 60, 90, 180, 365].map(d => (
                      <button
                        key={d}
                        type="button"
                        className="adm-btn adm-btn-secondary adm-btn-sm"
                        onClick={() => handleRenewDaysShortcut(d)}
                        style={{ fontWeight: renewModal.form.days_to_add === d ? 700 : 400 }}
                      >
                        +{d} dias {d === 365 ? '(1 ano)' : ''}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="adm-form-group">
                  <label>Nova Data de Expiração *</label>
                  <input
                    name="new_expires_at"
                    type="date"
                    value={renewModal.form.new_expires_at}
                    onChange={handleRenewFormChange}
                    required
                  />
                </div>

                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', marginTop: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      name="create_installment"
                      checked={renewModal.form.create_installment}
                      onChange={handleRenewFormChange}
                    />
                    Gerar parcela de cobrança referente a esta renovação
                  </label>

                  {renewModal.form.create_installment && (
                    <div className="adm-form-grid" style={{ marginTop: '0.75rem' }}>
                      <div className="adm-form-group">
                        <label>Valor da Cobrança (R$)</label>
                        <input
                          name="amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={renewModal.form.amount}
                          onChange={handleRenewFormChange}
                          required
                        />
                      </div>
                      <div className="adm-form-group">
                        <label>Data de Vencimento</label>
                        <input
                          name="due_date"
                          type="date"
                          value={renewModal.form.due_date}
                          onChange={handleRenewFormChange}
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="adm-modal-footer">
                <button
                  type="button"
                  className="adm-btn adm-btn-secondary"
                  onClick={() => setRenewModal(prev => ({ ...prev, open: false }))}
                  disabled={renewModal.saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="adm-btn adm-btn-success"
                  disabled={renewModal.saving}
                >
                  {renewModal.saving ? 'Renovando...' : 'Confirmar Renovação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Pagamentos ─────────────────────────────────────────────────────────
function PagamentosTab({ company }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showCharge, setShowCharge] = useState(null); // payment object
  const [form, setForm] = useState({ amount: '', due_date: '', reference_month: '', status: 'pending', notes: '', subscription_id: '', system: 'financial' });
  const [chargeForm, setChargeForm] = useState({ method: 'pix', 'customer.name': '', 'customer.email': '', 'customer.cpf': '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getPayments(company.id),
      getSubscription(company.id).catch(() => null),
    ]).then(([p, s]) => {
      setPayments(p.data || []);
      setSubscription(s);
    }).finally(() => setLoading(false));
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleChargeFormChange = (e) => {
    const { name, value } = e.target;
    setChargeForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        subscription_id: subscription?.id || null,
        system: form.system || 'financial',
      };
      await addPayment(company.id, payload);
      setShowAdd(false);
      setForm({ amount: '', due_date: '', reference_month: '', status: 'pending', notes: '', subscription_id: '', system: 'financial' });
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao adicionar pagamento.' });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (payment) => {
    try {
      await updatePayment(company.id, payment.id, { status: 'paid', paid_at: new Date().toISOString() });
      load();
    } catch {
      alert('Erro ao marcar como pago.');
    }
  };

  const handleCharge = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        method: chargeForm.method,
        customer: {
          name: chargeForm['customer.name'],
          email: chargeForm['customer.email'],
          cpf: chargeForm['customer.cpf'],
        },
      };
      const result = await chargePayment(company.id, showCharge.id, payload);
      setMsg({ type: 'success', text: `Cobrança criada! ${result.pagseguro_response?.payment_link || ''}` });
      setShowCharge(null);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Erro ao cobrar.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="adm-loading">Carregando pagamentos...</div>;

  return (
    <div>
      {msg && (
        <div className={`adm-alert ${msg.type === 'error' ? 'adm-alert-danger' : ''}`}
          style={msg.type === 'success' ? { background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem' } : {}}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="adm-btn adm-btn-primary" onClick={() => setShowAdd(!showAdd)}>
          + Registrar Pagamento
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd}>
          <div className="adm-card">
            <h3>Novo Pagamento</h3>
            <div className="adm-form-grid">
              <div className="adm-form-group">
                <label>Sistema Referente</label>
                <select name="system" value={form.system || 'financial'} onChange={handleFormChange}>
                  <option value="financial">💼 Sistema Financeiro</option>
                  <option value="fleet">🚛 Gestão de Frotas</option>
                  <option value="task">📋 Gerenciador de Tarefas</option>
                </select>
              </div>
              <div className="adm-form-group">
                <label>Valor (R$) *</label>
                <input name="amount" type="number" step="0.01" min="0" value={form.amount} onChange={handleFormChange} required />
              </div>
              <div className="adm-form-group">
                <label>Vencimento *</label>
                <input name="due_date" type="date" value={form.due_date} onChange={handleFormChange} required />
              </div>
              <div className="adm-form-group">
                <label>Referência (mês)</label>
                <input name="reference_month" type="month" value={form.reference_month} onChange={handleFormChange} placeholder="2026-02" />
              </div>
              <div className="adm-form-group">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleFormChange}>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="failed">Falhou</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>
              <div className="adm-form-group full-width">
                <label>Notas</label>
                <textarea name="notes" value={form.notes} onChange={handleFormChange} rows={2} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button type="submit" className="adm-btn adm-btn-success" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setShowAdd(false)}>Cancelar</button>
            </div>
          </div>
        </form>
      )}

      {payments.length === 0 ? (
        <div className="adm-empty"><div className="adm-empty-icon">💳</div><p>Nenhum pagamento registrado.</p></div>
      ) : (
        <div className="adm-card" style={{ padding: 0 }}>
          <div className="adm-table-wrapper">
            <table className="adm-table adm-table--cards">
              <thead>
                <tr>
                  <th>Sistema</th>
                  <th>Referência</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Pago em</th>
                  <th>Método</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td data-label="Sistema" style={{ fontSize: '0.85rem' }}>
                      {p.system === 'fleet' ? '🚛 Frotas' : p.system === 'task' ? '📋 Tarefas' : '💼 Financeiro'}
                    </td>
                    <td data-label="Referência" style={{ fontFamily: 'monospace' }}>{p.reference_month || '—'}</td>
                    <td data-label="Vencimento">{formatDate(p.due_date)}</td>
                    <td data-label="Valor">{formatCurrency(p.amount)}</td>
                    <td data-label="Status"><StatusBadge status={p.status} type="payment" /></td>
                    <td data-label="Pago em" data-hide-mobile="true" style={{ fontSize: '0.8rem' }}>{formatDateTime(p.paid_at)}</td>
                    <td data-label="Método" data-hide-mobile="true" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>{p.payment_method || '—'}</td>
                    <td data-label="Ações">
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {p.status === 'pending' && (
                          <>
                            <button className="adm-btn adm-btn-success adm-btn-sm" onClick={() => handleMarkPaid(p)}>✓ Pago</button>
                            <button className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => { setShowCharge(p); setMsg(null); }}>PagSeguro</button>
                          </>
                        )}
                        {p.pagseguro_payment_link && (
                          <a href={p.pagseguro_payment_link} target="_blank" rel="noreferrer" className="adm-btn adm-btn-secondary adm-btn-sm">Link</a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charge Modal */}
      {showCharge && (
        <div className="adm-modal-overlay" onClick={() => setShowCharge(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>Cobrar via PagSeguro</h3>
              <button className="adm-modal-close" onClick={() => setShowCharge(null)}>✕</button>
            </div>
            <form onSubmit={handleCharge}>
              <div className="adm-modal-body">
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Pagamento ref. {showCharge.reference_month} — {formatCurrency(showCharge.amount)}
                </p>
                <div className="adm-form-grid">
                  <div className="adm-form-group full-width">
                    <label>Método</label>
                    <select name="method" value={chargeForm.method} onChange={handleChargeFormChange}>
                      <option value="pix">PIX</option>
                      <option value="boleto">Boleto</option>
                    </select>
                  </div>
                  <div className="adm-form-group">
                    <label>Nome do pagador *</label>
                    <input name="customer.name" value={chargeForm['customer.name']} onChange={handleChargeFormChange} required />
                  </div>
                  <div className="adm-form-group">
                    <label>E-mail *</label>
                    <input name="customer.email" type="email" value={chargeForm['customer.email']} onChange={handleChargeFormChange} required />
                  </div>
                  <div className="adm-form-group">
                    <label>CPF *</label>
                    <input name="customer.cpf" value={chargeForm['customer.cpf']} onChange={handleChargeFormChange} placeholder="000.000.000-00" required />
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setShowCharge(null)}>Cancelar</button>
                <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>{saving ? 'Processando...' : 'Cobrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Usuários ───────────────────────────────────────────────────────────
function UsuariosTab({ company }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', password_confirmation: '', is_master: true });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [generatedPwd, setGeneratedPwd] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getCompanyUsers(company.id).then(setUsers).catch(console.error).finally(() => setLoading(false));
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  const isFirstUser = users.length === 0;

  const handleOpenAdd = () => {
    setForm({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      is_master: users.length === 0,
    });
    setShowAdd(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    setGeneratedPwd(null);

    // Validações de senha no frontend se informada manualmente
    if (form.password) {
      if (form.password.length < 8) {
        setMsg({ type: 'error', text: 'A senha deve conter no mínimo 8 caracteres.' });
        setSaving(false);
        return;
      }
      if (form.password !== form.password_confirmation) {
        setMsg({ type: 'error', text: 'A confirmação da senha não confere com a senha informada.' });
        setSaving(false);
        return;
      }
    }

    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password || undefined,
        password_confirmation: form.password ? form.password_confirmation : undefined,
        is_master: isFirstUser ? true : !!form.is_master,
      };
      const result = await addCompanyUser(company.id, payload);
      if (result.generated_password) {
        setGeneratedPwd(result.generated_password);
      }
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', password_confirmation: '', is_master: false });
      load();
      setMsg({ type: 'success', text: result.message || `Usuário criado: ${result.user.email}` });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setMsg({ type: 'error', text: errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Erro ao criar usuário.') });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateCompanyUser(company.id, user.id, { is_active: !user.is_active });
      load();
    } catch {
      alert('Erro ao atualizar status do usuário.');
    }
  };

  const handleMakeMaster = async (user) => {
    if (!window.confirm(`Deseja definir "${user.name}" como Usuário Master desta empresa? Ele terá acesso total e permissões de administração em todos os sistemas contratados.`)) return;
    try {
      await updateCompanyUser(company.id, user.id, { is_master: true });
      load();
      setMsg({ type: 'success', text: `"${user.name}" agora é Usuário Master da empresa.` });
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao atualizar usuário.');
    }
  };

  if (loading) return <div className="adm-loading">Carregando usuários...</div>;

  return (
    <div>
      {msg && (
        <div className={`adm-alert ${msg.type === 'error' ? 'adm-alert-danger' : ''}`}
          style={msg.type === 'success' ? { background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '0.5rem' } : {}}>
          {msg.text}
        </div>
      )}
      {generatedPwd && (
        <div className="adm-alert adm-alert-warning" style={{ marginBottom: '1rem' }}>
          <strong>Senha gerada automaticamente:</strong> <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{generatedPwd}</code>
          <br /><small>Copie e envie ao usuário. Ela não será exibida novamente.</small>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="adm-btn adm-btn-primary" onClick={handleOpenAdd}>+ Novo Usuário</button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd}>
          <div className="adm-card">
            <h3>{isFirstUser ? 'Novo Usuário (Master da Empresa)' : 'Novo Usuário'}</h3>

            {isFirstUser ? (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1.25rem', color: '#92400e', fontSize: '0.85rem', lineHeight: 1.5 }}>
                👑 <strong>Primeiro Usuário da Empresa:</strong> Este usuário será criado automaticamente como <strong>Usuário Master</strong>, com acesso irrestrito e permissões totais para gerenciar a empresa em todos os sistemas contratados (Financeiro, Frotas e Tarefas).
              </div>
            ) : (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    name="is_master"
                    checked={form.is_master}
                    onChange={handleChange}
                    style={{ width: 16, height: 16 }}
                  />
                  👑 Definir como Usuário Master da empresa (acesso irrestrito a todos os sistemas)
                </label>
              </div>
            )}

            <div className="adm-form-grid">
              <div className="adm-form-group">
                <label>Nome *</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="adm-form-group">
                <label>E-mail *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required />
              </div>
              <div className="adm-form-group">
                <label>Senha</label>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Mínimo 8 caracteres (ou deixe em branco)"
                  autoComplete="new-password"
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 2, display: 'block' }}>
                  Deixe em branco para gerar senha automaticamente.
                </small>
              </div>
              <div className="adm-form-group">
                <label>Confirmar Senha {form.password ? '*' : ''}</label>
                <input
                  name="password_confirmation"
                  type="password"
                  value={form.password_confirmation}
                  onChange={handleChange}
                  placeholder={form.password ? "Digite a senha novamente" : "Preencha a senha ao lado primeiro"}
                  autoComplete="new-password"
                  required={!!form.password}
                  disabled={!form.password}
                  style={!form.password ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="submit" className="adm-btn adm-btn-success" disabled={saving}>{saving ? 'Criando...' : 'Criar Usuário'}</button>
              <button
                type="button"
                className="adm-btn adm-btn-secondary"
                onClick={() => {
                  setShowAdd(false);
                  setForm({ name: '', email: '', password: '', password_confirmation: '', is_master: false });
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>
      )}

      {users.length === 0 ? (
        <div className="adm-empty"><div className="adm-empty-icon">👥</div><p>Nenhum usuário nesta empresa.</p></div>
      ) : (
        <div className="adm-card" style={{ padding: 0 }}>
          <div className="adm-table-wrapper">
            <table className="adm-table adm-table--cards">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Sistemas Liberados</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td data-label="Nome" style={{ fontWeight: 500 }}>
                      {u.name}
                    </td>
                    <td data-label="E-mail" style={{ color: '#6b7280' }}>{u.email}</td>
                    <td data-label="Perfil">
                      {u.is_super_admin ? (
                        <span className="adm-badge" style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.75rem', border: '1px solid #fde68a' }}>
                          👑 Master
                        </span>
                      ) : (
                        <span className="adm-badge" style={{ background: '#f3f4f6', color: '#4b5563', fontSize: '0.75rem' }}>
                          Colaborador
                        </span>
                      )}
                    </td>
                    <td data-label="Sistemas">
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(u.financial_active || u.is_super_admin) && (
                          <span className="adm-badge" style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#d1fae5', color: '#065f46' }}>
                            💼 Financeiro
                          </span>
                        )}
                        {(u.task_active || u.is_super_admin) && (
                          <span className="adm-badge" style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#ede9fe', color: '#5b21b6' }}>
                            📋 Tarefas
                          </span>
                        )}
                        {u.is_super_admin && (
                          <span className="adm-badge" style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#dbeafe', color: '#1e40af' }}>
                            🚛 Frotas
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Status">
                      <span className={`adm-badge ${u.is_active ? 'adm-badge-active' : 'adm-badge-suspended'}`}>
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td data-label="Criado em" data-hide-mobile="true" style={{ fontSize: '0.8rem' }}>{formatDate(u.created_at)}</td>
                    <td data-label="Ações">
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                        <button
                          className={`adm-btn adm-btn-sm ${u.is_active ? 'adm-btn-danger' : 'adm-btn-success'}`}
                          onClick={() => handleToggleActive(u)}
                        >
                          {u.is_active ? 'Desativar' : 'Ativar'}
                        </button>
                        {!u.is_super_admin && (
                          <button
                            className="adm-btn adm-btn-sm adm-btn-secondary"
                            onClick={() => handleMakeMaster(u)}
                            title="Promover a Usuário Master da empresa"
                          >
                            👑 Tornar Master
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
const TABS = [
  { key: 'dados', label: 'Dados' },
  { key: 'assinatura', label: 'Sistemas & Planos' },
  { key: 'pagamentos', label: 'Pagamentos' },
  { key: 'usuarios', label: 'Usuários' },
];

export default function AdminCompanyDetailPage() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dados');

  useEffect(() => {
    setLoading(true);
    getAdminCompany(id)
      .then(setCompany)
      .catch(e => setError(e.response?.data?.message || 'Empresa não encontrada.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="adm-loading">Carregando empresa...</div>;
  if (error) return <div className="adm-alert adm-alert-danger">{error}</div>;
  if (!company) return null;

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <div className="adm-breadcrumb">
            <Link to="/admin">Dashboard</Link> / <Link to="/admin/companies">Empresas</Link> / {company.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{company.name}</h2>
            {company.document_number && (
              <span style={{ fontSize: '0.85rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 6 }}>
                CNPJ/CPF: {company.document_number}
              </span>
            )}
          </div>

          {/* System Badges in Header */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.5rem' }}>
            {SYSTEMS_CONFIG.map(sys => {
              const sub = (company.subscriptions || []).find(s => s.system === sys.id);
              const isIntegral = sub?.access_type === 'integral';
              const isExpired = sub && !isIntegral && sub.expires_at && new Date(sub.expires_at) < new Date();
              const isActive = sub?.status === 'active' && !isExpired;

              let label = 'Não Contratado';
              let badgeBg = '#f3f4f6';
              let badgeColor = '#6b7280';

              if (sub) {
                if (isActive) {
                  label = isIntegral ? `${sub.plan_name || 'Ativo'} (Vitalício)` : `${sub.plan_name || 'Ativo'}`;
                  badgeBg = '#d1fae5';
                  badgeColor = '#065f46';
                } else if (sub.status === 'trial') {
                  label = 'Trial';
                  badgeBg = '#fef3c7';
                  badgeColor = '#92400e';
                } else if (isExpired) {
                  label = 'Expirado';
                  badgeBg = '#fee2e2';
                  badgeColor = '#991b1b';
                } else if (sub.status === 'suspended') {
                  label = 'Suspenso';
                  badgeBg = '#fee2e2';
                  badgeColor = '#991b1b';
                } else {
                  label = sub.status;
                  badgeBg = '#e5e7eb';
                  badgeColor = '#374151';
                }
              }

              return (
                <span
                  key={sys.id}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '3px 9px',
                    borderRadius: 12,
                    background: badgeBg,
                    color: badgeColor,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: `1px solid ${badgeColor}25`,
                  }}
                  title={`${sys.title}: ${label}`}
                >
                  <span>{sys.icon}</span> {sys.title.replace('Sistema ', '')}: {label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="adm-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`adm-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'dados' && <DadosTab company={company} onUpdated={setCompany} />}
      {activeTab === 'assinatura' && <AssinaturaTab company={company} onUpdated={setCompany} />}
      {activeTab === 'pagamentos' && <PagamentosTab company={company} />}
      {activeTab === 'usuarios' && <UsuariosTab company={company} />}
    </div>
  );
}
